import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Send, Bot, User, Sparkles, Clock, HelpCircle, RefreshCw } from 'lucide-react';

function MessageBubble({ message, isUser }) {
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-primary' : 'bg-primary/10'
      }`}>
        {isUser ? (
          <User className="h-4 w-4 text-primary-foreground" />
        ) : (
          <Bot className="h-4 w-4 text-primary" />
        )}
      </div>
      <div className={`max-w-[75%] ${isUser ? 'text-right' : ''}`}>
        <div className={`rounded-2xl px-4 py-2.5 ${
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'
        }`}>
          <p className="text-sm whitespace-pre-wrap">{message.text}</p>
        </div>
        {message.queryType && !isUser && (
          <Badge variant="secondary" className="mt-1.5 text-[10px]">
            {message.queryType}
          </Badge>
        )}
        <p className="text-[10px] text-muted-foreground mt-1">
          {new Date(message.timestamp).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}

function SuggestionCard({ text, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-sm"
      data-testid={`suggestion-${text.slice(0, 20).replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start gap-2">
        <HelpCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
        <span>{text}</span>
      </div>
    </button>
  );
}

function ResultsCard({ response }) {
  if (!response) return null;
  
  const { queryType, summary, items, count, total, active } = response;

  return (
    <Card className="mt-3 bg-muted/30">
      <CardContent className="p-4">
        {summary && (
          <p className="text-sm text-muted-foreground mb-3">{summary}</p>
        )}
        {(count !== undefined || total !== undefined) && (
          <div className="flex gap-4 text-sm mb-3">
            {count !== undefined && <span><strong>{count}</strong> results</span>}
            {total !== undefined && <span><strong>{total}</strong> total</span>}
            {active !== undefined && <span><strong>{active}</strong> active</span>}
          </div>
        )}
        {items && items.length > 0 && (
          <div className="space-y-2">
            {items.slice(0, 5).map((item, i) => (
              <div key={i} className="text-xs bg-background rounded p-2 flex justify-between items-center">
                <span className="font-medium">{item.name || item.participantName || item.staffName || item.houseName || item.type || 'Item'}</span>
                {item.score !== undefined && (
                  <Badge variant="outline" className="text-[10px]">Score: {item.score}</Badge>
                )}
                {item.riskLevel && (
                  <Badge variant={item.riskLevel === 'HIGH' ? 'destructive' : 'secondary'} className="text-[10px]">
                    {item.riskLevel}
                  </Badge>
                )}
                {item.status && (
                  <Badge variant="secondary" className="text-[10px]">{item.status}</Badge>
                )}
              </div>
            ))}
            {items.length > 5 && (
              <p className="text-xs text-muted-foreground">...and {items.length - 5} more</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const SUGGESTED_QUERIES = [
  'Which participants are high risk',
  'Which claims were rejected last week',
  'Which staff compliance expires soon',
  'How many participants are active',
  'Show recent incidents',
  'Which houses have open incidents',
];

export default function AICopilotPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [queryTypes, setQueryTypes] = useState([]);
  const [history, setHistory] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Fetch supported query types
    api.get('/ai/query/types')
      .then(res => setQueryTypes(res.data.data?.supportedQueries || {}))
      .catch(() => {});
    
    // Fetch history
    api.get('/ai/query/history', { params: { limit: 10 } })
      .then(res => setHistory(res.data.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (query) => {
    const queryText = query || input.trim();
    if (!queryText) return;

    const userMessage = {
      text: queryText,
      isUser: true,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/ai/query', { query: queryText });
      const data = res.data.data;
      
      const botMessage = {
        text: data.response?.summary || 'Query processed',
        isUser: false,
        queryType: data.queryType,
        response: data.response,
        timestamp: data.createdAt || new Date().toISOString()
      };
      setMessages(prev => [...prev, botMessage]);
      
      // Refresh history
      api.get('/ai/query/history', { params: { limit: 10 } })
        .then(res => setHistory(res.data.items || []))
        .catch(() => {});
    } catch (err) {
      toast.error('Failed to process query');
      const errorMessage = {
        text: err.response?.data?.detail || 'Sorry, I couldn\'t process that query. Please try again.',
        isUser: false,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="h-[calc(100vh-10rem)] flex gap-6" data-testid="ai-copilot-page">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        <div className="mb-4">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            AI Copilot
          </h1>
          <p className="text-muted-foreground">Ask questions about your data in natural language</p>
        </div>

        <Card className="flex-1 flex flex-col">
          {/* Messages Area */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <Bot className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">How can I help you today?</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                  Ask me about participants, staff, risk scores, compliance, funding, or incidents.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                  {SUGGESTED_QUERIES.slice(0, 4).map((q, i) => (
                    <SuggestionCard key={i} text={q} onClick={() => handleSubmit(q)} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((m, i) => (
                  <div key={i}>
                    <MessageBubble message={m} isUser={m.isUser} />
                    {!m.isUser && m.response && <ResultsCard response={m.response} />}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="bg-muted rounded-2xl px-4 py-2.5">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="border-t p-4">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                placeholder="Ask a question..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
                className="flex-1"
                data-testid="query-input"
              />
              <Button onClick={() => handleSubmit()} disabled={loading || !input.trim()} data-testid="send-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Sidebar */}
      <div className="w-80 flex-shrink-0 space-y-4">
        {/* Suggested Questions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Suggested Questions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {SUGGESTED_QUERIES.map((q, i) => (
              <button
                key={i}
                onClick={() => handleSubmit(q)}
                className="w-full text-left text-xs p-2 rounded hover:bg-muted transition-colors"
                data-testid={`sidebar-suggestion-${i}`}
              >
                {q}
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Recent History */}
        {history.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Recent Queries
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {history.slice(0, 5).map((h, i) => (
                <button
                  key={i}
                  onClick={() => handleSubmit(h.queryText)}
                  className="w-full text-left text-xs p-2 rounded hover:bg-muted transition-colors truncate"
                  data-testid={`history-item-${i}`}
                >
                  {h.queryText}
                </button>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
